import { z } from 'zod';
import type { BerichtKennzahlen, BerichtSummen, BerichtTag, BerichtZeile } from './berichte';
import type { KampagneTyp } from './kampagnen';
import type { WkzPhase, WkzStatus } from './wkz';

/**
 * Retail Media: Übersicht im Konto (Klick auf „Retail Media“ in der Seitenleiste) und im Portal des Werbetreibenden
 * (Reiter „Übersicht“ unter Werbung, nur das eigene Werbekonto). Werbeumsatz aus Klicks und
 * WKZ, Verlauf, Formate, Top-Kampagnen und -Werbekonten, offene Aufgaben und die nächsten Startseiten-Buchungen.
 * Gelesen werden nur der Tages-Rollup und kleine Tabellen (Werbekonten, Kampagnen, Buchungen) – nie Ereignisse.
 */

export const DASHBOARD_ZEITRAEUME = { 7: '7 Tage', 30: '30 Tage', 90: '90 Tage' } as const;
export type DashboardZeitraum = 7 | 30 | 90;

export const WerbeDashboardAbfrage = z.object({
  tage: z.coerce.number().int().refine((t): t is DashboardZeitraum => t === 7 || t === 30 || t === 90, 'Zeitraum 7, 30 oder 90 Tage').default(30),
});

/** Unter diesem verfügbaren Guthaben gilt ein Werbekonto mit laufender Kampagne als „fast leer“ */
export const GUTHABEN_NIEDRIG_CENT = 1_000;

export interface WerbeDashboardFormat {
  format: KampagneTyp;
  kampagnenAktiv: number;
  impressionen: number;
  klicks: number;
  kostenCent: number;
  umsatzCent: number;
}

export interface WerbeDashboardWkz {
  id: string;
  platzierung: string;
  werbekonto: string;
  von: string;
  bis: string;
  status: WkzStatus;
  phase: WkzPhase;
  preisCent: number;
}

export interface WerbeDashboard {
  tage: DashboardZeitraum;
  von: string;
  bis: string;
  /** Kennzahlen aus dem Rollup im Zeitraum (Kosten = Klickerlöse des Marktplatzes) */
  summen: BerichtKennzahlen;
  /** gleich langer Zeitraum davor – für die Trends */
  vorher: BerichtSummen;
  /** bestätigte WKZ-Buchungen mit Beginn im Zeitraum (Festpreis) */
  wkzCent: number;
  wkzVorherCent: number;
  reihe: BerichtTag[];
  formate: WerbeDashboardFormat[];
  topKampagnen: BerichtZeile[];
  topWerbekonten: BerichtZeile[];
  werbekonten: { aktiv: number; gesperrt: number; guthabenCent: number; aufladungenCent: number };
  kampagnen: { aktiv: number; pausiert: number; entwurf: number };
  /**
   * Werbemittel in Prüfung (beim Marktplatz) bzw. abgelehnt (zum Überarbeiten), WKZ-Anfragen, Werbekonten mit laufender
   * Kampagne und fast leerem Guthaben. WKZ-Aufgaben je Seite: wkzWerbemittel = Werbemittel erstellen bzw. überarbeiten,
   * wkzFreigabe = Werbemittel der anderen Seite freigeben, wkzLive = bereit zum Live-Stellen (nur Marktplatz).
   */
  aufgaben: {
    werbemittelPruefung: number;
    werbemittelAbgelehnt: number;
    wkzAngefragt: number;
    wkzWerbemittel: number;
    wkzFreigabe: number;
    wkzLive: number;
    guthabenNiedrig: number;
    /** Werbekonten, deren letzte Abbuchung fehlgeschlagen ist – ihre Kampagnen pausieren */
    abbuchungFehlgeschlagen: number;
  };
  /** Werbemittel gehören zum Werbekonto: wartende Prüfungen je Werbekonto, die meisten zuerst */
  werbemittelJeKonto: { werbekontoId: string; name: string; anzahl: number }[];
  /** offene WKZ-Buchungen (angefragt, bestätigt, live) ab heute, nach Beginn */
  wkzKommend: WerbeDashboardWkz[];
  berechnetAm: string | null;
}
